import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AwardKickoffWizard from "@/pages/dashboard/fs/wizards/AwardKickoff";
import { WhoDoesIt, whoBlocked, performWho } from "@/pages/dashboard/fs/WhoDoesIt";
import { JobSequence, ScheduleEmailLine } from "@/pages/dashboard/fs/JobSequence";
import { ScopeCard } from "@/pages/dashboard/fs/ScopeCard";
import { heldToast } from "@/components/fs/SendWindowNote";
import {
  Loader2, ClipboardList, Search, AlertTriangle, ShieldAlert, FileSignature,
  CircleDollarSign, CheckCircle2, XCircle, Plus, Camera, StickyNote, RefreshCw, Ban,
  ListChecks, ArrowRight, Check, Trash2,
} from "lucide-react";
import { V } from "@/lib/vocab";

const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};
const STATE = {
  draft: ["bg-sand-200 text-ink/70", "Draft"],
  pending_paperwork: ["bg-amber-50 text-amber-800", "Waiting on paperwork"],
  active: ["bg-emerald-50 text-emerald-700", "Cleared to start"],
  in_progress: ["bg-indigo-50 text-indigo-700", "In progress"],
  complete: ["bg-sand-200 text-ink/60", "Complete"],
  cancelled: ["bg-sand-200 text-ink/40", "Cancelled"],
};
const KIND = { one_off: "One-off", recurring: "Recurring", project: "Project" };
// iter371 — while the company is still coming on board, the row says so instead of "waiting on paperwork".
const STAGE_PILL = {
  invited: ["bg-indigo-50 text-indigo-700", "Invited"],
  setting_up: ["bg-indigo-50 text-indigo-700", "Setting up"],
  needs_approval: ["bg-amber-50 text-amber-800", "Approve the company"],
};
// The crew's side of the same job. Only the states a manager needs to act on get a chip.
const DISPATCH = {
  no_response: ["bg-red-50 text-red-700", "No response"],
  overdue: ["bg-orange-50 text-orange-700", "Accepted · overdue"],
  completed_unverified: ["bg-amber-50 text-amber-800", "Done · no photo"],
  enroute: ["bg-indigo-50 text-indigo-700", "On the way"],
};
const PAPER = { coi: "insurance certificate", w9: "W-9", agreement: "signed agreement", vendor: "a company to do the work" };

/* A reason box that isn't window.prompt (iter370). */
function ReasonDialog({ open, onOpenChange, title, body, placeholder, confirmLabel, tone = "red", onConfirm, busy, testid }) {
  const [why, setWhy] = useState("");
  useEffect(() => { if (open) setWhy(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid={testid}>
        <DialogHeader className="text-left">
          <DialogTitle className="font-heading text-xl font-extrabold">{title}</DialogTitle>
        </DialogHeader>
        {body && <p className="text-sm text-ink/65">{body}</p>}
        <textarea data-testid={`${testid}-reason`} value={why} onChange={(e) => setWhy(e.target.value)} rows={3} placeholder={placeholder}
          className="w-full resize-none rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none focus:border-ink" />
        <div className="flex justify-end gap-2">
          <button data-testid={`${testid}-cancel`} onClick={() => onOpenChange(false)} disabled={busy}
            className="rounded-xl border border-sand-300 bg-white px-4 py-2 text-sm font-bold text-ink/70">Keep it</button>
          <button data-testid={`${testid}-confirm`} onClick={() => onConfirm(why.trim())} disabled={busy}
            className={`rounded-xl px-4 py-2 text-sm font-bold text-white disabled:opacity-50 ${tone === "red" ? "bg-redline-500 hover:bg-redline-600" : "bg-ink hover:bg-ink-700"}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* "Assign a company" from an open job — the same three doors as the New-job wizard. */
function AssignCompany({ tenantId, wo, onDone }) {
  const [who, setWho] = useState({ mode: "", vendor_id: "", invite: {}, quote_ids: [] });
  const [busy, setBusy] = useState(false);
  const blocked = whoBlocked(who);
  const go = async () => {
    setBusy(true);
    try {
      const res = await performWho(tenantId, wo.id, who);
      toast.success(who.mode === "invite" ? (heldToast(res?.held, `${res?.work_order?.vendor_name || "The company"}'s invite`) || `${res?.work_order?.vendor_name || "The company"} invited — the job is theirs when their paperwork clears`)
        : who.mode === "quotes" ? `${who.quote_ids.length} compan${who.quote_ids.length === 1 ? "y" : "ies"} asked to price it`
          : `Assigned to ${res?.work_order?.vendor_name || "the company"}`);
      onDone?.();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Try again");
    }
    setBusy(false);
  };
  return (
    <div className="rounded-2xl border border-redline-200 bg-redline-50/40 p-4" data-testid="wo-assign-company">
      <div className="mb-3 text-sm font-bold text-ink">No company yet — who's doing it?</div>
      <WhoDoesIt tenantId={tenantId} category={wo.category} choice={who} onChange={setWho} allowLater={false} autoPick />
      <button data-testid="wo-assign-go" disabled={!!blocked || busy} onClick={go} title={blocked || ""}
        className="mt-3 inline-flex items-center gap-2 rounded-xl bg-ink px-5 py-2.5 text-sm font-bold text-white disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        {who.mode === "invite" ? "Invite & assign" : who.mode === "quotes" ? "Ask for quotes" : "Assign"}
      </button>
      {blocked && who.mode && <span className="ml-3 text-xs text-ink/50">{blocked}</span>}
    </div>
  );
}
const usd = (n) => (n === null || n === undefined || n === "" ? "—"
  : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const when = (s) => (s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export const StatePill = ({ status, stage }) => {
  const [cls, label] = STAGE_PILL[stage] || STATE[status] || ["bg-sand-200 text-ink/60", status || "—"];
  return <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${cls}`}>{label}</span>;
};

const Row = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-sand-100 py-2 last:border-0">
    <span className="shrink-0 text-xs font-bold uppercase tracking-wider text-ink/40">{label}</span>
    <span className="min-w-0 text-right text-sm text-ink/80">{children}</span>
  </div>
);


/* ── detail ────────────────────────────────────────────────────────────────── */
function Detail({ tenantId, woId, readOnly, onChanged, onBack, onGoTo }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [punch, setPunch] = useState("");
  const [code, setCode] = useState("");
  const [kick, setKick] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [declineCo, setDeclineCo] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/tenants/${tenantId}/field-services/wos/${woId}`);
    setD(data); setCode(data.work_order.gl_code || "");
  }, [tenantId, woId]);
  useEffect(() => { load().catch(() => toast.error("Couldn't load that work order")); }, [load]);

  if (!d) return <div className="grid h-48 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-ink/30" /></div>;
  const w = d.work_order;
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await load(); onChanged?.(); }
    catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : det?.message || "Try again");
    }
    setBusy(false);
  };
  const openPunch = (w.punch_list || []).filter((p) => !p.done);
  // iter371 — where the job stands with its company decides what's shown. Nothing about money,
  // confirming or the punch list until the company is Ready (approved + paperwork in).
  const vs = w.vendor_stage || {};
  const stage = vs.stage || "unassigned";
  const ready = !!vs.ready;
  const confirmed = ["confirmed", "started", "done"].includes(stage);
  const showMoney = ready || (w.amount !== null && w.amount !== undefined && w.amount !== "") || w.invoice_total > 0;
  const paperMissing = (w.paperwork_missing || []).filter((k) => k !== "vendor");

  if (kick) return (
    <AwardKickoffWizard tenantId={tenantId} woId={woId} onClose={() => setKick(false)}
      onDone={async () => { await load(); onChanged?.(); }} />
  );

  return (
    <div className="space-y-5" data-testid={`wo-detail-${woId}`}>
      <div className="flex flex-wrap items-center gap-2">
        <StatePill status={w.status} stage={stage} />
        <span className="rounded-full bg-sand-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-ink/55">{KIND[w.kind] || w.kind}</span>
        <span className="font-mono text-xs font-bold text-ink/40">{w.ref}</span>
        {w.code_locked && <span className="rounded-full bg-sand-200 px-2.5 py-0.5 text-[11px] font-bold uppercase text-ink/50">Code locked</span>}
      </div>

      {!w.vendor_id && !["cancelled", "complete"].includes(w.status) && (!readOnly || w.can_author) && (
        <AssignCompany tenantId={tenantId} wo={w} onDone={async () => { await load(); onChanged?.(); }} />
      )}

      <JobSequence wo={w} busy={busy} readOnly={readOnly}
        onSendNow={() => act(() => api.post(`/tenants/${tenantId}/field-services/vendors/${w.vendor_id}/send-held-invite`), `Invite sent to ${w.vendor_name}`)}
        onRecheck={() => act(() => api.post(`/tenants/${tenantId}/field-services/wos/${woId}/action/recheck`, {}), "Re-checked")}
        onApprove={() => { onBack?.(); onGoTo?.("vendors"); }} />

      {w.vendor_id && confirmed && paperMissing.length > 0 && (
        <div data-testid="wo-paperwork-banner" className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="min-w-0 text-xs leading-relaxed text-amber-900">
            <b>Waiting on {w.vendor_name}'s</b> {paperMissing.map((k) => PAPER[k] || k).join(" and ")} — they've been asked. Nobody is sent to the property until it's in.
            {w.paperwork_due && <> They said by <b>{String(w.paperwork_due).slice(0, 10)}</b>.</>}
            {!readOnly && (
              <button data-testid="wo-recheck" onClick={() => act(
                () => api.post(`/tenants/${tenantId}/field-services/wos/${woId}/action/recheck`, {}), "Re-checked")}
                className="ml-1 inline-flex items-center gap-1 font-bold underline">
                <RefreshCw className="h-3 w-3" /> check again
              </button>
            )}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-sand-200 bg-white px-4 py-2">
        <Row label="Work">{w.title}</Row>
        <Row label="Company">{w.vendor_name || <span className="text-amber-700">No company yet</span>}</Row>
        <Row label="Trade">{CAT_LABEL[w.category] || w.category || "—"}</Row>
        {w.location && <Row label="Where">{w.location}</Row>}
        {showMoney && <Row label="Price">{w.price_pending ? <span className="text-ink/50">not agreed yet</span> : usd(w.amount)}</Row>}
        {showMoney && w.deposit ? <Row label="Deposit">{usd(w.deposit)}</Row> : null}
        {showMoney && w.fee?.amount ? <Row label="Management fee">{usd(w.fee.amount)} <span className="text-xs text-ink/40">(not shown to the vendor)</span></Row> : null}
        {(confirmed || w.invoice_total > 0) && <Row label="Invoiced & approved">{usd(w.invoice_total || 0)}</Row>}
        {w.start_window && <Row label="Starts">{w.start_window}</Row>}
        {w.project_id && !w.proposal_id && <Row label="Quotes">requested{w.quotes_due ? ` · due ${w.quotes_due}` : ""} — pick the winner under Projects</Row>}
        <Row label="Opened">{when(w.created_at)}</Row>
      </div>

      {(w.scope || (w.scope_files || []).length > 0 || w.can_author) && (
        <ScopeCard tenantId={tenantId} kind="wo" id={woId} doc={w} testid="wo-scope"
          canAuthor={!!w.can_author && !["complete", "cancelled"].includes(w.status)} onChanged={load}
          draftCtx={{ title: w.title, category: w.category, location: w.location, priority: w.priority, project_id: w.project_id }} />
      )}

      {d.settings?.accounting_codes && ready && (
      <div>
        <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">Accounting code</div>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="wo-code" value={code} disabled={readOnly || w.code_locked}
            onChange={(e) => setCode(e.target.value)}
            className="min-w-0 flex-1 rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm outline-none disabled:bg-sand-100">
            <option value="">— no code yet —</option>
            {(d.codes || []).map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
          </select>
          {!readOnly && !w.code_locked && (
            <button data-testid="wo-code-save" disabled={busy || !code || code === w.gl_code}
              onClick={() => act(() => api.post(`/tenants/${tenantId}/field-services/wos/${woId}/code`,
                { gl_code: code }), "Coded")}
              className="rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40">Save</button>
          )}
        </div>
        {w.code_locked && <p className="mt-1 text-xs text-ink/45">An invoice on this job is already approved, so the code is locked.</p>}
      </div>
      )}

      {!readOnly && stage === "ready" && !w.kicked_off_at && ["draft", "pending_paperwork", "active"].includes(w.status) && (
        <button data-testid="wo-kickoff-open" onClick={() => setKick(true)}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-redline-500 px-5 py-3 text-sm font-bold text-white hover:bg-redline-600">
          <Check className="h-4 w-4" /> Confirm the job — price, start, contract
        </button>
      )}

      {confirmed && w.vendor_id && (
        <ScheduleEmailLine wo={w} busy={busy} readOnly={readOnly}
          onSend={() => act(async () => {
            const { data } = await api.post(`/tenants/${tenantId}/field-services/wos/${woId}/schedule-email`);
            const who = `${(data.to || []).join(", ")}${(data.cc || []).length ? ` · cc ${(data.cc || []).join(", ")}` : ""}`;
            if (data.sent) toast.success(`Emailed ${who}`);
            else toast.warning(`Addressed to ${who} — ${data.why || "not delivered"}`);
          })} />
      )}

      {(w.contract || {}).path && (
        <div data-testid="wo-contract" className="rounded-2xl border border-sand-200 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="inline-flex items-center gap-1.5 text-sm font-bold text-ink">
              <FileSignature className="h-4 w-4 text-ink/40" /> Contract
            </div>
            <span className="text-xs font-semibold text-ink/55">
              {{ none: "not started", awaiting_vendor: "waiting on their paper",
                vendor_uploaded: "their contract is in — read it, then send it",
                out_for_signature: "out for signature", signed: "signed by everyone",
                skipped: "waived" }[w.contract.status] || w.contract.status}
            </span>
          </div>
          {w.contract.why && <p className="mt-1 text-xs text-ink/50">{w.contract.why}</p>}
          {!readOnly && w.contract.status === "vendor_uploaded" && (
            <button data-testid="wo-contract-send" onClick={() => act(
              () => api.post(`/tenants/${tenantId}/field-services/wos/${woId}/contract/send`, {}),
              "Sent — the vendor signs first")}
              className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white">
              <FileSignature className="h-3.5 w-3.5" /> Send for signature
            </button>
          )}
          {!readOnly && ["none", "awaiting_vendor", "vendor_uploaded"].includes(w.contract.status) && (
            <button data-testid="wo-contract-skip" onClick={() => act(
              () => api.post(`/tenants/${tenantId}/field-services/wos/${woId}/contract/skip`,
                { reason: "waived by the manager" }), "Waived")}
              className="ml-2 mt-2 text-xs font-semibold text-ink/50 underline">skip the contract</button>
          )}
        </div>
      )}

      {(w.change_orders || []).length > 0 && (
        <div data-testid="wo-change-orders">
          <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">Change orders</div>
          {w.change_orders.map((c) => (
            <div key={c.id} className="mb-2 rounded-xl border border-sand-200 bg-white px-4 py-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-ink">{usd(c.amount)}</div>
                  <div className="text-xs text-ink/55">{c.description}</div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                  c.status === "approved" ? "bg-emerald-50 text-emerald-700"
                    : c.status === "rejected" ? "bg-red-50 text-red-700"
                      : c.status === "board_review" ? "bg-indigo-50 text-indigo-700" : "bg-amber-50 text-amber-800"}`}>
                  {c.status === "board_review" ? `With the ${V.grp()}` : c.status}
                </span>
              </div>
              {!readOnly && ["submitted", "board_review"].includes(c.status) && (
                <div className="mt-2 flex gap-2">
                  <button data-testid={`co-approve-${c.id}`} onClick={() => act(async () => {
                    const { data } = await api.post(
                      `/tenants/${tenantId}/field-services/wos/${woId}/change-orders/${c.id}/approve`, {});
                    if (data.needs_board) toast.info(`That takes the job over the ${V.grp()} threshold — sent to the ${V.grp()}`);
                  })} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white">Approve</button>
                  <button data-testid={`co-reject-${c.id}`} onClick={() => setDeclineCo(c)}
                    className="rounded-lg border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/60">Decline</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {(confirmed || (w.punch_list || []).length > 0) && (
      <div data-testid="wo-punch">
        <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">
          Punch list {openPunch.length ? `· ${openPunch.length} open` : ""}
        </div>
        {(w.punch_list || []).map((p) => (
          <label key={p.id} className="mb-1.5 flex items-center gap-2.5 rounded-xl border border-sand-200 bg-white px-4 py-2.5">
            <input type="checkbox" checked={!!p.done} disabled={readOnly}
              data-testid={`punch-${p.id}`}
              onChange={() => act(() => api.post(`/tenants/${tenantId}/field-services/wos/${woId}/punch`,
                { op: "toggle", item_id: p.id }))} />
            <span className={`min-w-0 flex-1 text-sm ${p.done ? "text-ink/35 line-through" : "text-ink/80"}`}>{p.text}</span>
          </label>
        ))}
        {!readOnly && (
          <div className="mt-1 flex gap-2">
            <input data-testid="punch-input" value={punch} placeholder="What still isn't right?"
              onChange={(e) => setPunch(e.target.value)}
              className="min-w-0 flex-1 rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none" />
            <button data-testid="punch-add" disabled={!punch.trim() || busy} onClick={() => act(async () => {
              await api.post(`/tenants/${tenantId}/field-services/wos/${woId}/punch`, { op: "add", text: punch });
              setPunch("");
            }, "Added — the vendor was told")}
              className="rounded-xl bg-ink px-3 py-2 text-sm font-bold text-white disabled:opacity-40">
              <Plus className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
      )}

      {(w.progress || []).length > 0 && (
        <div data-testid="wo-progress">
          <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">Updates from the crew</div>
          {w.progress.slice().reverse().map((p) => (
            <div key={p.id} className="mb-2 rounded-xl bg-sand-50 px-4 py-3">
              <div className="text-xs font-bold text-ink/45">{p.by} · {when(p.at)}</div>
              {p.note && <p className="mt-0.5 text-sm text-ink/75">{p.note}</p>}
              {(p.photos || []).length > 0 && (
                <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-ink/45">
                  <Camera className="h-3.5 w-3.5" /> {(p.photos || []).length} photo(s)
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {(d.visits || []).length > 0 && (
        <div data-testid="wo-visits">
          <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">Visits</div>
          {d.visits.slice(0, 12).map((v) => (
            <div key={v.id} className="mb-1.5 flex items-center justify-between gap-2 rounded-xl border border-sand-200 bg-white px-4 py-2.5 text-sm">
              <span className="text-ink/75">{v.date} · {v.name}</span>
              <span className="text-xs font-semibold text-ink/45">{v.status}{v.queue_count ? ` · ${v.queue_count} reported` : ""}</span>
            </div>
          ))}
        </div>
      )}

      {(d.invoices || []).length > 0 && (
        <div data-testid="wo-invoices">
          <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">Invoices</div>
          {d.invoices.map((i) => (
            <div key={i.id} className="mb-1.5 flex items-center justify-between gap-2 rounded-xl border border-sand-200 bg-white px-4 py-2.5 text-sm">
              <span className="text-ink/75">{usd(i.amount)} · {i.type}{i.number ? ` · #${i.number}` : ""}</span>
              <span className="text-xs font-semibold text-ink/45">{i.status}</span>
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <div className="flex flex-wrap gap-2 border-t border-sand-200 pt-4">
          {!["complete", "cancelled"].includes(w.status) && ready && (
            <button data-testid="wo-finish" onClick={() => act(async () => {
              try { await api.post(`/tenants/${tenantId}/field-services/wos/${woId}/finish`, {}); }
              catch (e) {
                const det = e?.response?.data?.detail;
                if (det?.can_force && window.confirm(`${det.message} Complete it anyway?`)) {
                  await api.post(`/tenants/${tenantId}/field-services/wos/${woId}/finish`, { force: true });
                } else throw e;
              }
            }, `Completed — the ${V.grp()} recap is on its way`)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white">
              <CheckCircle2 className="h-4 w-4" /> Mark complete
            </button>
          )}
          {!["complete", "cancelled"].includes(w.status) && (w.invoice_total > 0 || ["in_progress"].includes(w.status)) && (
            <button data-testid="wo-cancel" onClick={() => setCancelOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-4 py-2 text-sm font-bold text-ink/60">
              <Ban className="h-4 w-4" /> Call it off
            </button>
          )}
          {!(w.invoice_total > 0) && !["in_progress", "complete"].includes(w.status) && (
            <button data-testid="wo-delete" onClick={() => setDeleteOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-4 py-2 text-sm font-bold text-ink/60 hover:border-redline-300 hover:text-redline-600">
              <Trash2 className="h-4 w-4" /> Delete this job
            </button>
          )}
        </div>
      )}

      <ReasonDialog open={cancelOpen} onOpenChange={setCancelOpen} testid="wo-cancel-dialog" busy={busy}
        title="Call this job off?" body={`${w.title}${w.vendor_name ? ` · ${w.vendor_name}` : ""}. The record stays; the company is told.`}
        placeholder="Why (optional)" confirmLabel="Call it off"
        onConfirm={(reason) => act(async () => {
          await api.post(`/tenants/${tenantId}/field-services/wos/${woId}/action/cancel`, { reason });
          setCancelOpen(false);
        }, "Called off")} />
      <ReasonDialog open={!!declineCo} onOpenChange={(o) => { if (!o) setDeclineCo(null); }} testid="co-decline-dialog" busy={busy}
        title="Decline this change?" body={declineCo ? `${declineCo.title || "Change order"} · ${usd(declineCo.amount)}` : ""}
        placeholder="Tell the company why" confirmLabel="Decline" tone="ink"
        onConfirm={(reason) => {
          if (!reason) { toast.error("Give them a reason."); return; }
          act(async () => {
            await api.post(`/tenants/${tenantId}/field-services/wos/${woId}/change-orders/${declineCo.id}/reject`, { reason });
            setDeclineCo(null);
          }, "Declined");
        }} />
      <Dialog open={deleteOpen} onOpenChange={(o) => { if (!busy) setDeleteOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="wo-delete-dialog">
          <DialogHeader className="text-left"><DialogTitle className="font-heading text-xl font-extrabold">Delete this job?</DialogTitle></DialogHeader>
          <p className="text-sm text-ink/65">{w.title}. It disappears from Work orders{w.vendor_id ? ` and ${w.vendor_name} won't hear about it again` : ""}. Nothing has been invoiced, so there's no record to keep.</p>
          <div className="flex justify-end gap-2">
            <button data-testid="wo-delete-cancel" onClick={() => setDeleteOpen(false)} disabled={busy} className="rounded-xl border border-sand-300 bg-white px-4 py-2 text-sm font-bold text-ink/70">Keep it</button>
            <button data-testid="wo-delete-confirm" disabled={busy} onClick={async () => {
              setBusy(true);
              try {
                await api.delete(`/tenants/${tenantId}/field-services/wos/${woId}`);
                toast.success("Deleted"); setDeleteOpen(false); onChanged?.(); onBack?.();
              } catch (e) { const det = e?.response?.data?.detail; toast.error(typeof det === "string" ? det : "Couldn't delete"); }
              setBusy(false);
            }} className="rounded-xl bg-redline-500 px-4 py-2 text-sm font-bold text-white hover:bg-redline-600 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <div data-testid="wo-audit">
        <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">History</div>
        {(w.audit || []).slice().reverse().map((a, i) => (
          <div key={i} className="flex gap-3 border-b border-sand-100 py-1.5 text-xs last:border-0">
            <span className="w-28 shrink-0 text-ink/35">{when(a.at)}</span>
            <span className="min-w-0 flex-1 text-ink/70"><b>{a.event.replace(/_/g, " ")}</b>{a.detail ? ` — ${a.detail}` : ""}</span>
            <span className="shrink-0 text-ink/35">{a.actor}</span>
          </div>
        ))}
        {!readOnly && (
          <div className="mt-2 flex gap-2">
            <input data-testid="wo-note-input" value={note} placeholder="Add a note to the record"
              onChange={(e) => setNote(e.target.value)}
              className="min-w-0 flex-1 rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none" />
            <button data-testid="wo-note-add" disabled={!note.trim() || busy} onClick={() => act(async () => {
              await api.post(`/tenants/${tenantId}/field-services/wos/${woId}/action/note`, { note });
              setNote("");
            })} className="rounded-xl bg-ink px-3 py-2 text-sm font-bold text-white disabled:opacity-40">
              <StickyNote className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── list ──────────────────────────────────────────────────────────────────── */

/* Real work that isn't a commitment yet. It gets a signpost, never a row — the moment
   uncommitted work joins the list, "what have we committed to" stops having an answer. */
function InFlight({ d, onGoTo }) {
  if (!d) return null;
  const days = (iso) => Math.ceil((new Date(`${iso}T23:59:59`) - new Date()) / 86400000);
  const closing = d.closing?.bids_due ? days(d.closing.bids_due) : null;
  const items = [];

  if (d.open_projects > 0) {
    items.push({
      key: "projects", to: "projects", icon: ClipboardList,
      head: `${d.open_projects} project${d.open_projects === 1 ? "" : "s"} out for bids`,
      sub: d.stale > 0
        ? `${d.stale} gone quiet with no bids — extend, widen, or go direct`
        : d.past_due > 0
          ? `${d.past_due} past its deadline — decide whether to extend or go direct`
          : closing === null ? "No deadline set — we'll chase it on age instead"
            : closing <= 0 ? `“${d.closing.title}” closes today`
              : closing === 1 ? `“${d.closing.title}” closes tomorrow`
                : `Soonest closes in ${closing} days`,
      warn: d.stale > 0 || d.past_due > 0 || (closing !== null && closing <= 1),
    });
  }
  if (d.queue_waiting > 0) {
    items.push({
      key: "queue", to: "queue", icon: ListChecks,
      head: `${d.queue_waiting} report${d.queue_waiting === 1 ? "" : "s"} waiting for a crew's next visit`,
      sub: d.queue_rolled > 0
        ? `${d.queue_rolled} already rolled past a visit without being done`
        : "On the list, nobody pulled out for them",
      warn: d.queue_rolled > 0,
    });
  }
  if (!items.length) return null;

  return (
    <div className="grid gap-2 sm:grid-cols-2" data-testid="wo-in-flight">
      {items.map((it) => (
        <button key={it.key} data-testid={`wo-in-flight-${it.key}`} onClick={() => onGoTo?.(it.to)}
          className={`group flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 text-left transition-colors ${it.warn
            ? "border-amber-200 bg-amber-50/70 hover:border-amber-300" : "border-sand-200 bg-white hover:border-ink/25"}`}>
          <it.icon className={`mt-0.5 h-4 w-4 shrink-0 ${it.warn ? "text-amber-600" : "text-ink/35"}`} />
          <span className="min-w-0">
            <span className="block text-xs font-bold text-ink">{it.head}</span>
            <span className="block text-[11px] leading-snug text-ink/55">{it.sub}</span>
          </span>
          <ArrowRight className="ml-auto mt-0.5 h-3.5 w-3.5 shrink-0 text-ink/25 transition-transform group-hover:translate-x-0.5" />
        </button>
      ))}
    </div>
  );
}

export default function WorkOrders({ tenantId, readOnly, onGoTo }) {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("live");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  // "Open the work order" links (desk-copy emails) land straight on that record.
  const [open, setOpen] = useState(() => new URLSearchParams(window.location.search).get("wo") || null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/tenants/${tenantId}/field-services/wos`, {
      params: { status: filter, kind, q, limit: 80 },
    });
    setD(data);
  }, [tenantId, filter, kind, q]);
  useEffect(() => { load().catch(() => toast.error("Couldn't load the work orders")); }, [load]);

  if (!d) return <div className="grid h-48 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-ink/30" /></div>;
  const c = d.counts || {};
  const FILTERS = [["live", `Live · ${(c.draft || 0) + (c.pending_paperwork || 0) + (c.active || 0) + (c.in_progress || 0)}`],
    ["pending_paperwork", `Waiting on paperwork · ${c.pending_paperwork || 0}`],
    ["draft", `Draft · ${c.draft || 0}`],
    ["complete", `Complete · ${c.complete || 0}`],
    ["", "Everything"]];

  return (
    <div className="space-y-4" data-testid="fs-work-orders">
      <p className="rounded-xl bg-sand-100 px-4 py-3 text-xs leading-relaxed text-ink/65">
        Every commitment to a company lives here — a one-off job, a recurring service or an awarded
        project. Each one carries the budget line it's charged to and the paperwork gate that says
        whether they're allowed on the property today.
      </p>

      <InFlight d={d.in_flight} onGoTo={onGoTo} />

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([k, label]) => (
          <button key={k || "all"} data-testid={`wo-filter-${k || "all"}`} onClick={() => setFilter(k)}
            className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${filter === k
              ? "bg-ink text-white" : "bg-sand-100 text-ink/55 hover:bg-sand-200"}`}>{label}</button>
        ))}
        <select data-testid="wo-kind" value={kind} onChange={(e) => setKind(e.target.value)}
          className="rounded-full border border-sand-300 bg-white px-3 py-1.5 text-xs font-bold text-ink/60 outline-none">
          <option value="">Any type</option>
          {Object.entries(KIND).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink/30" />
          <input data-testid="wo-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search"
            className="w-44 rounded-full border border-sand-300 py-1.5 pl-8 pr-3 text-xs outline-none" />
        </div>
      </div>

      {!(d.work_orders || []).length ? (
        <div data-testid="wo-empty" className="rounded-2xl border border-dashed border-sand-300 py-12 text-center">
          <ClipboardList className="mx-auto h-7 w-7 text-ink/20" />
          <p className="mt-2 text-sm text-ink/50">Nothing here yet.</p>
          <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-ink/40">
            Work orders appear the moment you send a request to a vendor, set up a recurring service,
            or award a project.
          </p>
        </div>
      ) : (d.work_orders || []).map((w) => (
        <button key={w.id} data-testid={`wo-row-${w.id}`} onClick={() => setOpen(w.id)}
          className="flex w-full flex-wrap items-center justify-between gap-3 rounded-2xl border border-sand-200 bg-white px-4 py-3 text-left transition-colors hover:border-sand-300">
          <div className="min-w-[12rem] flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[11px] font-bold text-ink/35">{w.ref}</span>
              <StatePill status={w.status} stage={(w.vendor_stage || {}).stage} />
              <span className="rounded-full bg-sand-100 px-2 py-0.5 text-[10px] font-bold uppercase text-ink/50">{KIND[w.kind] || w.kind}</span>
              {(w.paperwork_missing || []).length > 0 && !STAGE_PILL[(w.vendor_stage || {}).stage] && (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700">
                  <AlertTriangle className="h-3 w-3" /> {(w.paperwork_missing || []).map((k) => PAPER[k] || k).join(", ")}
                </span>
              )}
              {w.open_invoices > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700">
                  <CircleDollarSign className="h-3 w-3" /> {w.open_invoices} invoice(s) to review
                </span>
              )}
              {DISPATCH[(w.dispatch || {}).status] && (
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${DISPATCH[w.dispatch.status][0]}`}
                  data-testid={`wo-dispatch-${w.id}`}>
                  {DISPATCH[w.dispatch.status][1]}
                </span>
              )}
            </div>
            <div className="mt-0.5 truncate text-sm font-semibold text-ink">{w.title}</div>
            <div className="truncate text-xs text-ink/45">
              {w.vendor_name || <span className="font-semibold text-amber-700">no company yet — assign one</span>}
              {w.gl_code ? ` · ${w.gl_code} ${w.gl_name || ""}` : " · not coded yet"}
              {w.location ? ` · ${w.location}` : ""}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-sm font-bold text-ink">{usd(w.amount)}</div>
            {w.invoice_total ? <div className="text-[11px] text-ink/45">{usd(w.invoice_total)} approved</div> : null}
          </div>
        </button>
      ))}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Work order</DialogTitle></DialogHeader>
          {open && <Detail tenantId={tenantId} woId={open} readOnly={readOnly} onChanged={load} onBack={() => setOpen(null)} onGoTo={onGoTo} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { XCircle };
